import { deriveProductSemanticTags, complementFamiliesFromSemanticFamily } from '@/lib/menuSemanticTags';
import { pickVariant, AB_RECOMMENDATION_COPY } from '@/lib/assistantABTesting';
import { burgerStrategy, pickAssistantCompositionStrategy } from '@/lib/assistantFamilyStrategies';
import {
  getProductIncludedItems,
  getProductIncludedSummary,
  getProductUpsellItems,
  productHasExplicitAssistantBundle,
  productIncludedHasDrink,
  productIncludedHasFries,
  productIsBurgerish,
} from '@/lib/productComposition';
import { getAssistantTraining } from '@/lib/assistantTrainingKnowledge';
import { ORDER_INTENT_RX } from '@/lib/assistant/assistantRegionalSpanish';
import {
  normalizeAssistantText,
  ASSISTANT_STOP_WORDS,
} from '@/lib/assistant/assistantTextUtils';
import {
  rankProductMatches,
  productIngredientSearchBlob,
  visibleMenuProducts,
  matchProductCategory,
  filterProductsByCategoryKeys,
  productTypeQueryIntent,
  findProductsStrictIngredientTokens,
  isIngredientAvailabilityQuery,
  answerIngredientAvailabilityQuestion,
  bestProductMatchForAssistantQuery,
  resolveStrongProductMention,
  resolveProductsByIds,
} from '@/lib/assistant/assistantProductSearch';

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const INGREDIENT_META_TERM_BLOCKLIST = new Set(
  normalizeAssistantText(
    'menu menú carta plato platos dia día del dia del día recomienda recomiendas recomienden sugeris sugerís sugerime'
  )
    .split(/\s+/)
    .filter(Boolean)
);

function semanticTagsForProduct(product, menuData) {
  if (!product) return deriveProductSemanticTags({}, null, null);
  return deriveProductSemanticTags(
    product,
    menuData?.business ?? null,
    (menuData?.promos || []).filter((pr) => pr.is_active !== false)
  );
}

function preferFastFoodIntent(n) {
  return (
    /\b(algo\s+r[aá]pido|r[aá]pido\s+para\s+comer|opciones\s+r[aá]pidas|comida\s+r[aá]pida|que\s+sea\s+r[aá]pido|pedir\s+r[aá]pido)\b/.test(
      n
    ) && !/\b(llegue|llegar|lleguen|delivery|min|minutos|demoren|demora)\b/.test(n)
  );
}

function mergeProductListsPreservingOrder(primary, secondary) {
  const seen = new Set((primary || []).map((p) => String(p?.id)));
  const out = [...(primary || [])].filter(Boolean);
  for (const p of secondary || []) {
    if (!p || seen.has(String(p.id))) continue;
    seen.add(String(p.id));
    out.push(p);
  }
  return out;
}


/**
/** @param {object[]} products */


function listAddOns(product) {
  const a = product?.add_ons;
  if (!Array.isArray(a) || a.length === 0) return null;
  return a.map((x) => {
    const name = x?.name || '';
    const price = Number(x?.price) || 0;
    return price > 0 ? `${name} (+$${price.toLocaleString('es-CL')})` : name;
  }).filter(Boolean);
}

function describeProductComposition(product, queryNorm, menuData, business) {
  const biz = business ?? menuData?.business ?? {};
  const md = menuData || { business: biz, promos: [] };
  const strat = pickAssistantCompositionStrategy(product, biz, md);
  return strat.describeComposition({ product, queryNorm, menuData: md, business: biz });
}

/**
 * Extrae el fragmento de personalización (sin/con, etc.) del texto del usuario.
 * Base: (sin|con)\\s+(.+); amplía pero sin, extra, sacar/quitar + objeto.
 */
export function extractCustomizationNote(text) {
  const raw = String(text || '').trim();
  const mSin = raw.match(/\bsin\s+(.+)/i);
  const mCon = raw.match(/\bcon\s+(.+)/i);
  const iSin = mSin ? raw.search(/\bsin\s+/i) : -1;
  const iCon = mCon ? raw.search(/\bcon\s+/i) : -1;
  if (iSin >= 0 && (iCon < 0 || iSin <= iCon)) {
    return normalizeNiInCustomization(mSin[0].trim());
  }
  if (iCon >= 0 && mCon) {
    return normalizeNiInCustomization(mCon[0].trim());
  }
  const m2 = raw.match(/pero\s+sin\s+(.+)/i);
  if (m2) return normalizeNiInCustomization(`sin ${m2[1]}`.trim());
  const m3 = raw.match(/\bextra\s+(.+)/i);
  if (m3) return normalizeNiInCustomization(`extra ${m3[1]}`.trim());
  const m4 = raw.match(/\b(sacar|quitar)\s+(.+)/i);
  if (m4) return normalizeNiInCustomization(`${m4[1]} ${m4[2]}`.trim());
  return null;
}

function normalizeNiInCustomization(s) {
  const t = String(s || '').trim().replace(/\s+/g, ' ');
  if (/^sin\b/i.test(t)) {
    return t.replace(/\s+ni\s+/gi, ' y sin ');
  }
  if (/^con\b/i.test(t)) {
    return t.replace(/\s+ni\s+/gi, ' y con ');
  }
  return t.replace(/\s+ni\s+/gi, ' y ');
}

export function formatOrderNoteFromCustomizationSnippet(snippet) {
  const s = String(snippet || '').trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function hasOrderInstructionIntent(n) {
  if (/\bla\s+quiero\b/i.test(n)) return true;
  return ORDER_INTENT_RX.test(n);
}

export function hasCustomizationInstructionShape(n, trimmed) {
  return /(sin\s+.+|con\s+.+|pero\s+sin|extra\s+.+|sacar|quitar)/i.test(trimmed) ||
    /(sin\s+.+|con\s+.+|pero\s+sin|extra\s+.+|sacar|quitar)/i.test(n);
}

/** Preguntas por composición: no convertir en nota de pedido. */
export function isCompositionConsultationOnly(n, trimmed) {
  const t = trimmed.trim();
  if (!/\?\s*$/.test(t)) return false;
  if (hasOrderInstructionIntent(n) && hasCustomizationInstructionShape(n, trimmed)) return false;
  if (/\b(qu[eé]|q)\s+(trae|lleva|tiene)\b/.test(n)) return true;
  if (/\b(que lleva|qué lleva|que trae|qué trae|ingredientes)\b/.test(n)) return true;
  if (/\btiene\b/.test(n)) return true;
  if (/\bse\s+puede\s+(sacar|quitar|cambiar|personaliz|modificar)\b/.test(n)) return true;
  return false;
}

function findDietaryMatchesSubstrings(products, substrings) {
  const out = [];
  for (const p of products) {
    if (p.availability === 'hidden') continue;
    const blob = normalizeAssistantText(`${p.name || ''} ${p.description || ''} ${p.category || ''}`);
    if (substrings.some((s) => blob.includes(s))) out.push(p);
  }
  return out;
}

/** Palabras que no son búsqueda de ítem en carta. */
const SIMPLE_LOOKUP_BLOCKLIST = new Set(
  normalizeAssistantText(
    'delivery envio envío domicilio reparto pedido menu menú precio promo oferta horario whatsapp hola gracias chau si no buenas tardes buenos dias noches chau ok vale bien carta cartas diario plato recomienda recomiendas recomienden sugeris sugerís sugerime'
  ).split(/\s+/)
);

/** “Rico / bueno” etc. no son ingredientes para lookup literal. */
const TASTE_OPINION_TOKEN_BLOCK = new Set(
  normalizeAssistantText(
    'rico rica ricos riquis riquisimo bueno buena buenos buenas delicioso deliciosa piola picao brutal'
  )
    .split(/\s+/)
    .filter(Boolean)
);

/** Intención de recomendación suelta (“algo rico”, “qué tiene de bueno”). */
function looksLikeCasualRecommendationQuery(n, trimmed) {
  const t = String(trimmed || '').toLowerCase();
  if (
    /\b(recomienda|recomiendas|recomienden|recomendad|suger[ií]|sugerime)\w*\b/.test(n) ||
    /\b(algo\s+recomendado|lo\s+m[aá]s\s+recomendado)\b/.test(n)
  ) {
    return true;
  }
  if (
    /\b(algo\s+rico|algo\s+buen|algo\s+para\s+comer|qu[eé]\s+tiene[\w\s]{0,18}de\s+buen|qu[eé]\s+hay\s+de\s+buen)\b/.test(
      n
    )
  ) {
    return true;
  }
  if (/\b(hola|hey|buenas|buenos\s+dias|buenas\s+tardes)[\s,!.]*algo\s+(rico|bueno|buena|para\s+comer)\b/.test(t)) {
    return true;
  }
  if (
    /\b(rico|bueno|buena|delicioso|deliciosa|piola|picao)\b/.test(n) &&
    /\b(algo|un\w*|unas?|pedir|quiero|hay|tienen|tenes|tienes|dan|venden|mostrame|muestrame|muéstrame|mostrá|dame|decime)\b/.test(
      n
    )
  ) {
    return true;
  }
  return false;
}

export function isAffirmativeComplementReply(n, trimmed) {
  const t = trimmed.replace(/^[¿?]+\s*/, '').trim();
  if (t.length > 22) return false;
  return (
    /^(s[ií]|sip|dale|ok|okay|oka|vale|bueno|perfecto|vamos|claro|obvio)(\s*[!.])*$/i.test(t) ||
    /^(s[ií]|sip)\s*[,.]?\s*$/i.test(t)
  );
}

/** Respuesta corta negativa post-upsell (“no gracias”, “paso”) sin insistir. */
export function isShortUpsellDeclineReply(n, trimmed) {
  if (/\b(tienen|hay|horario|precio|men[uú]|carta|dónde|donde|cuánto|cuanto|whatsapp)\b/.test(n)) {
    return false;
  }
  const t = trimmed.replace(/^[¿?]+\s*/, '').trim();
  if (t.length > 28 || t.includes('?')) return false;
  return (
    /^(no|nop|nope|nah|nanai|paso|no\s*gracias|no[,.\s]+gracias|mejor\s+no|ah[ií]\s+no)(\s*[!.…])*$/i.test(t) ||
    /^no[,.\s]+gracias$/i.test(t)
  );
}

/** Términos tipo “menú / carta”, no ingredientes (normalizado sin acentos). */

function firstMeaningfulTokens(raw, maxCount) {
  const parts = String(raw || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const out = [];
  for (const p of parts) {
    const nt = normalizeAssistantText(p.replace(/^[,;.]+|[,;.]+$/g, ''));
    if (nt.length < 3 || ASSISTANT_STOP_WORDS.has(nt) || SIMPLE_LOOKUP_BLOCKLIST.has(nt)) continue;
    out.push(nt);
    if (out.length >= maxCount) break;
  }
  return out;
}

/**
 * “Pedir”, “seguir pidiendo”, etc.: intención de seguir comprando, no producto literal “pedir”.
 */
export function isContinueShoppingShortcutIntent(n, trimmed) {
  const t = String(trimmed || '').trim();
  if (!t || t.length > 56) return false;
  if (/\b(no\s+quiero|mejor\s+no)\b/.test(n)) return false;
  const low = normalizeAssistantText(t.replace(/^🛒\s*/u, '').trim());
  if (
    /^(pedir|quiero\s+pedir|seguir\s+pidiendo|seguir\s+comprando|agregar\s+mas|agregar\s+más|sumar\s+algo|algo\s+más|algo\s+mas)\s*$/i.test(
      low
    )
  ) {
    return true;
  }
  if (
    /\b(quiero\s+pedir|seguir\s+pidiendo|seguir\s+comprando|agregar\s+más|agregar\s+mas|sumar\s+algo)\b/.test(n) &&
    !/\b(busco|producto\s+llamado|que\s+se\s+llame|nombre)\b/.test(n)
  ) {
    return true;
  }
  return false;
}

/**
 * Consulta corta tipo ingrediente o producto (“pollo”, “pollo frito”).
 * No compite con preguntas largas ni con intents ya cubiertos arriba.
 */
function isSimpleMenuLookupQuery(trimmed, n) {
  if (!trimmed) return false;
  if (isContinueShoppingShortcutIntent(n, trimmed)) return false;
  if (looksLikeCasualRecommendationQuery(n, trimmed)) return false;
  if (/\b(recomienda|recomiendas|recomienden|suger[ií]|sugerime)\w*\b/.test(n)) return false;
  if (/\bqu[eé]\s+me\s+/.test(n)) return false;
  const deq = trimmed.replace(/^[¿?]+\s*|\s*[\s?.]+$/g, '').trim();
  if (!deq || deq.includes('?')) return false;
  if (/\b(como|cuando|cuándo|donde|dónde|por que|por qué|hay algo|tienen algo)\b/.test(n)) {
    return false;
  }
  if (/\b(menu\s+del\s+dia|menú\s+del\s+día|carta(\s+del\s+d(í|i)a)?)\b/.test(n)) {
    return false;
  }
  const meaningful = firstMeaningfulTokens(deq, 3);
  if (meaningful.length === 0 || meaningful.length > 2) return false;
  if (meaningful.some((t) => TASTE_OPINION_TOKEN_BLOCK.has(t))) return false;
  if (meaningful.length && meaningful.every((t) => INGREDIENT_META_TERM_BLOCKLIST.has(t))) return false;
  return true;
}


function answerSimpleMenuLookup(trimmed, products, defaultFallback, menuData = null) {
  const deq = trimmed.replace(/^[¿?]+\s*|\s*[\s?.]+$/g, '').trim();
  const tokens = firstMeaningfulTokens(deq, 2);
  if (!tokens.length) {
    return null;
  }
  const matches = findProductsStrictIngredientTokens(products, tokens, menuData);
  const label = tokens.join(' ');
  if (matches.length > 0) {
    return {
      text: `Sí, encontré esto con ${label} en el menú cargado 👇`,
      products: matches,
      fallbackKeys: defaultFallback,
    };
  }
  return {
    text: `No veo ${label} en el menú activo de hoy.`,
    products: null,
    fallbackKeys: defaultFallback,
  };
}


/**
 * Base de alias de ingredientes / cortes (normalizados). Un término del grupo expande la búsqueda a todo el grupo.
 * Incluye alias comunes y vocabulario sushi/japonés (ebi, maguro, sake = salmón, kani, tako, etc.).
 */

/** Recomendación abierta (“qué me recomiendas”), sin comparar solo dos ítems. */
function detectGenericRecommendationQuery(n, trimmed) {
  if (isIngredientAvailabilityQuery(n, trimmed)) return false;
  if (looksLikeCasualRecommendationQuery(n, trimmed)) return true;
  if (/\b(vegano|vegana|vegetariano|vegetariana|sin\s+gluten|celiac|celíac|celiaco|celiaca)\b/.test(n)) {
    return false;
  }
  if (/\b(no\s+quiero|mejor\s+no)\b/.test(n)) return false;
  return (
    /\b(recomi[eé]ndam\w*|recomendas\s+algo|recomiendas\s+algo)\b/.test(n) ||
    /\bme\s+recomiendas\b/.test(n) ||
    /\bme\s+recomienda\b/.test(n) ||
    /\bqu[eé]\s+me\s+recomiendas\b/.test(n) ||
    /\bqu[eé]\s+me\s+recomienda\b/.test(n) ||
    /\bqu[eé]\s+me\s+conviene\s+pedir\b/.test(n) ||
    /\b(qu[eé]\s+me\s+suger|suger[ií]me|decime\s+qu[eé]\s+pedir)\b/.test(n)
  );
}

function orderProductPoolByMemory(vis, memory, menuData, queryNorm) {
  const arr = [...vis];
  const wantFast = queryNorm && preferFastFoodIntent(queryNorm);
  if (memory?.priceSensitivity === 'cheap') {
    arr.sort(
      (a, b) =>
        (Number(a.price) || 0) - (Number(b.price) || 0) ||
        (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0)
    );
    return arr;
  }
  if (memory?.partySize >= 2) {
    const score = (p) => {
      const b = productIngredientSearchBlob(p);
      let s = 0;
      if (productSuggestsSharingOrGroup(p, menuData)) s += 1;
      if (/\bcombo\b/.test(b)) s += 1;
      if (menuData) {
        const t = semanticTagsForProduct(p, menuData);
        if (t.is_shared) s += 1;
        if (t.is_combo) s += 0.5;
      }
      return s;
    };
    arr.sort((a, b) => {
      const d = score(b) - score(a);
      if (d !== 0) return d;
      return (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0);
    });
    return arr;
  }
  if (wantFast && menuData) {
    arr.sort((a, b) => {
      const ta = semanticTagsForProduct(a, menuData).is_fast;
      const tb = semanticTagsForProduct(b, menuData).is_fast;
      if (ta !== tb) return (tb ? 1 : 0) - (ta ? 1 : 0);
      return (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0);
    });
    return arr;
  }
  arr.sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0));
  return arr;
}

function topSalesWithMemory(products, limit, memory, menuData, queryNorm) {
  return orderProductPoolByMemory(visibleMenuProducts(products), memory, menuData, queryNorm).slice(0, limit);
}

export function pickRecommendationProducts(products, resolvedCtx, limit, memory, menuData, queryNorm) {
  const vis = visibleMenuProducts(products);
  const seen = new Set();
  const out = [];
  for (const p of resolvedCtx || []) {
    if (p && p.availability !== 'hidden' && !seen.has(p.id)) {
      seen.add(p.id);
      out.push(p);
    }
  }
  const pool = orderProductPoolByMemory(vis, memory, menuData, queryNorm);
  const famKey = (p) => (menuData ? semanticTagsForProduct(p, menuData).family : null);
  for (const p of pool) {
    if (out.length >= limit) break;
    if (seen.has(p.id)) continue;
    const fk = famKey(p);
    if (fk) {
      const nSame = out.reduce((acc, x) => acc + (famKey(x) === fk ? 1 : 0), 0);
      if (nSame >= 2) continue;
    }
    seen.add(p.id);
    out.push(p);
  }
  return out.slice(0, limit);
}

function detectImplicitComboIntent(n) {
  if (/\b(no\s+quiero|sin\s+combo)\b/.test(n)) return false;
  if (/\bcombo\b/.test(n)) return true;
  const papa = /\b(papa|papas|frita|fritas|papas\s+fritas)\b/.test(n);
  const beb = /\b(bebida|gaseosa|jugo|refresco|coca|sprite|pepsi|fanta|agua\s+mineral|bebidas)\b/.test(n);
  if (papa && beb) return true;
  if (/\b(hamburguesa|burger|hamburg)\b/.test(n) && papa) return true;
  if (/\bacompañamiento\b/.test(n) && (papa || beb)) return true;
  return false;
}

function scoreImplicitComboProduct(p, menuData) {
  const b = productIngredientSearchBlob(p);
  let s = 0;
  if (/\bcombo\b/.test(b)) s += 12;
  if (/\b(menu|menú)\b.*\b(con|incluye|mas|más)\b/.test(b) || /\bincluye\b.*\b(bebida|papa|papas)\b/.test(b)) {
    s += 8;
  }
  const hp = /\b(papa|papas|frita|fritas)\b/.test(b);
  const hb =
    /\b(bebida|gaseosa|jugo|coca|sprite|pepsi|refresco|fanta)\b/.test(b) || /\+bebida/.test(b);
  if (hp && hb) s += 10;
  else if (hp) s += 4;
  else if (hb) s += 2;
  if (menuData) {
    const t = semanticTagsForProduct(p, menuData);
    if (t.is_combo) s += 6;
    if (t.has_drink && t.has_fries && !(hp && hb)) s += 8;
    else {
      if (t.has_fries && !hp) s += 4;
      if (t.has_drink && !hb) s += 3;
    }
  }
  return s;
}

export function answerImplicitComboIntent(n, trimmed, products, defaultFallback, menuData) {
  if (!detectImplicitComboIntent(n)) return null;
  const vis = visibleMenuProducts(products);
  const scored = vis
    .map((p) => ({ p, s: scoreImplicitComboProduct(p, menuData) }))
    .filter((x) => x.s >= 6)
    .sort(
      (a, b) =>
        b.s - a.s || (Number(b.p.sales_count) || 0) - (Number(a.p.sales_count) || 0)
    );
  if (scored.length) {
    return {
      text: 'Estos en la ficha suenan a combo o mencionan papas y bebida 👇',
      products: scored.map((x) => x.p).slice(0, 8),
      fallbackKeys: defaultFallback,
    };
  }
  const blob = (p) => productIngredientSearchBlob(p);
  const burgers = vis
    .filter((p) => /\b(hamburguesa|burger|hamburg|sandwich|sándwich)\b/.test(blob(p)))
    .sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0));
  const papasOnly = vis
    .filter(
      (p) =>
        /\b(papa|papas|frita|fritas|porcion|porción)\b/.test(blob(p)) &&
        !/\b(hamburguesa|burger|hamburg|sandwich|sándwich)\b/.test(blob(p))
    )
    .sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0));
  const drinks = vis
    .filter((p) =>
      /\b(bebida|gaseosa|jugo|coca|agua|sprite|pepsi|500\s*cc|lt|litro)\b/.test(blob(p))
    )
    .sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0));
  const merged = [];
  const seen = new Set();
  for (const p of [...burgers.slice(0, 2), ...papasOnly.slice(0, 2), ...drinks.slice(0, 2)]) {
    if (p && !seen.has(p.id)) {
      seen.add(p.id);
      merged.push(p);
    }
  }
  if (merged.length) {
    return {
      text: 'No veo un combo cerrado en todas las fichas; con esto lo armás rápido (base + papas + bebida) 👇',
      products: merged.slice(0, 8),
      fallbackKeys: defaultFallback,
    };
  }
  const top = topSalesProducts(products, 6);
  if (top.length) {
    return {
      text: 'No encontré combos claros en las descripciones; te dejo lo más pedido — en el carrito podés sumar papas o bebida si el local lo ofrece 👇',
      products: top,
      fallbackKeys: defaultFallback,
    };
  }
  return null;
}

const CRAVING_KEYWORDS = {
  dulce: ['dulce', 'chocolate', 'postre', 'brownie', 'helado', 'manjar', 'alfajor', 'torta', 'cake', 'galleta'],
  salado: ['salado', 'salada', 'salados'],
  crujiente: ['crujiente', 'crocante', 'frito', 'frita', 'fritos', 'empanizado'],
  queso: ['queso', 'mozzarella', 'cheddar', 'parmes', 'provoleta'],
};

function visibleProductsList(products) {
  return (products || []).filter((p) => p.availability !== 'hidden');
}

function productsByCraving(products, kind) {
  const keys = CRAVING_KEYWORDS[kind];
  if (!keys?.length) return [];
  const vis = visibleProductsList(products);
  const out = [];
  for (const p of vis) {
    const blob = productIngredientSearchBlob(p);
    if (keys.some((k) => blob.includes(k))) out.push(p);
  }
  out.sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0));
  return out.slice(0, 8);
}

function trimmedLengthOkForCraving(n) {
  return /\b(algo\s+dulce|algo\s+salado|algo\s+crujiente|algo\s+con\s+queso)\b/.test(n);
}

function detectCravingKind(n) {
  if (/\bquiero\s+algo\s+dulce\b/.test(n)) return 'dulce';
  if (/\balgo\s+con\s+queso\b/.test(n)) return 'queso';
  if (/\b(algo|antojo|gana|quiero)\b/.test(n) || trimmedLengthOkForCraving(n)) {
    if (/\bdulce\b/.test(n)) return 'dulce';
    if (/\bsalad[oa]s?\b/.test(n)) return 'salado';
    if (/\bcrujiente|crocante\b/.test(n)) return 'crujiente';
    if (/\bqueso\b/.test(n)) return 'queso';
  }
  return null;
}

function topSalesProducts(products, limit) {
  return [...visibleProductsList(products)]
    .sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0))
    .slice(0, limit);
}

function productSuggestsSharingOrGroup(p, menuData = null) {
  const s = productIngredientSearchBlob(p);
  if (
    /\b(para\s*2|para\s+dos|duo|dúo|familiar|grupo|compartir|2\s*pers|3\s*pers|tres\s+personas)\b/.test(s)
  ) {
    return true;
  }
  if (menuData && semanticTagsForProduct(p, menuData).is_shared) return true;
  return false;
}

function compareTwoProductsCopy(a, b) {
  const pa = Number(a.price) || 0;
  const pb = Number(b.price) || 0;
  const sa = Number(a.sales_count) || 0;
  const sb = Number(b.sales_count) || 0;
  const lines = [];
  if (pa > 0 && pb > 0) {
    if (pa < pb) {
      lines.push(
        `Por precio, ${a.name} sale más accesible ($${pa.toLocaleString('es-CL')} vs $${pb.toLocaleString('es-CL')}).`
      );
    } else if (pb < pa) {
      lines.push(
        `Por precio, ${b.name} sale más accesible ($${pb.toLocaleString('es-CL')} vs $${pa.toLocaleString('es-CL')}).`
      );
    } else {
      lines.push(`En precio están parejos ($${pa.toLocaleString('es-CL')}).`);
    }
  }
  if (sa > sb * 1.15 && sa >= 2) {
    lines.push(`${a.name} es de lo más pedido en este menú.`);
  } else if (sb > sa * 1.15 && sb >= 2) {
    lines.push(`${b.name} es de lo más pedido en este menú.`);
  }
  if (!lines.length) {
    lines.push(
      'Las dos son buena onda; si querés algo más liviano mirá la descripción de cada una y elegí con el hambre que traes 👇'
    );
  }
  return lines.join('\n');
}

/**
 * Intenciones comerciales (sin IA): exploración, cierre, antojo, comparación, dudas, cantidad, etc.
 */
export function tryCommerceLayerIntents(n, trimmed, products, business, defaultFallback, ranked, bestProduct, flowContext, menuData) {
  const memory = flowContext?.sessionMemory || {};
  const ids = flowContext?.lastSuggestedProductIds || [];
  const resolvedCtx = resolveProductsByIds(products, ids).filter((p) => p.availability !== 'hidden');

  const explorationHit = /\b(que\s+tienen|qué\s+tienen|que\s+hay|qué\s+hay|mostrame|muéstrame|mostráme|muestrame|mostrar\s+algo|muestra\s+algo|que\s+ofrecen|qué\s+ofrecen)\b/.test(
    n
  );
  const catFromQuery = matchProductCategory(n);

  if (explorationHit) {
    if (/\b(horario|precio\s+del|cuanto|cuánto|delivery|whatsapp)\b/.test(n)) return null;
    const catCount = (menuData?.categories || []).length;
    const extra =
      catCount > 0 ? ' Si querés algo más puntual, también podés recorrer por categorías.' : '';

    if (catFromQuery) {
      const typed = filterProductsByCategoryKeys(products, catFromQuery.keys, menuData);
      if (typed.length) {
        return {
          text: `Esto va sonando a ${catFromQuery.label} en lo que tenemos cargado 👇${extra}`,
          products: typed.slice(0, 8),
          fallbackKeys: ['categories_hint', 'catalog', 'order_hint', 'main'],
        };
      }
      const topFallback = topSalesWithMemory(products, 6, memory, menuData, n);
      if (topFallback.length) {
        return {
          text: `No veo ${catFromQuery.label} en el menú activo de hoy.${extra}\n\nSi querés otra cosa, esto es de lo más pedido (no es lo mismo): 👇`,
          products: topFallback,
          fallbackKeys: ['categories_hint', 'catalog', 'order_hint', 'main'],
        };
      }
    }

    const top = topSalesWithMemory(products, 6, memory, menuData, n);
    if (top.length) {
      return {
        text: `Mirá, esto es de lo que más piden por acá 👇${extra}`,
        products: top,
        fallbackKeys: ['categories_hint', 'catalog', 'order_hint', 'main'],
      };
    }
    return {
      text: 'Tengo el menú cargado; abrilo y elegí con calma, o pedime algo más específico (dulce, salado, con queso…).',
      products: null,
      fallbackKeys: ['catalog', 'categories_hint', 'order_hint', 'main'],
    };
  }

  if (catFromQuery && productTypeQueryIntent(n, trimmed) && !explorationHit) {
    const hits = filterProductsByCategoryKeys(products, catFromQuery.keys, menuData);
    if (hits.length) {
      return {
        text: `Sí 👌 encontré opciones que encajan con ${catFromQuery.label} en el menú activo 👇`,
        products: hits.slice(0, 8),
        fallbackKeys: defaultFallback,
      };
    }
    const alts = topSalesWithMemory(products, 4, memory, menuData, n);
    const altBit =
      alts.length > 0
        ? `\n\nNo es lo mismo que ${catFromQuery.label}, pero esto es de lo más pedido 👇`
        : '';
    return {
      text: `No veo ${catFromQuery.label} en el menú activo de hoy.${altBit}`,
      products: alts.length ? alts : null,
      fallbackKeys: ['catalog', 'categories_hint', 'main'],
    };
  }

  if (
    /\b(ya\s+listo|eso\s+nomas|eso\s+nomás|vamos\s+con\s+eso|listo\s+nomas|listo\s+nomás|eso\s+es\s+todo|cerramos\s+asi|cerramos\s+así|dale\s+asi|dale\s+así)\b/.test(
      n
    )
  ) {
    return {
      text: '¡Buenísimo! 👌 Revisá el carrito y cerramos cuando quieras.',
      products: null,
      fallbackKeys: ['checkout_cart', 'catalog', 'main'],
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  const craving = detectCravingKind(n);
  const spicyAsk =
    /\b(picante|spicy|enchilad|que\s+pique|con\s+chile|bien\s+picante|nivel\s+picante)\b/.test(n) &&
    !/\b(no\s+picante|sin\s+picante|nada\s+de\s+picante)\b/.test(n);
  if (spicyAsk) {
    if (/\b(horario|precio|delivery|cuanto|cuánto)\b/.test(n)) return null;
    const vis = visibleMenuProducts(products);
    const hits = vis
      .filter((p) => semanticTagsForProduct(p, menuData).is_spicy)
      .sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0))
      .slice(0, 8);
    if (hits.length) {
      return {
        text: '🌶️ Estas opciones suenan picantes o con salsa fuerte según la ficha 👇',
        products: hits,
        fallbackKeys: defaultFallback,
      };
    }
    return {
      text: '🌶️ No veo algo marcado como picante en las fichas; si manejás el nivel, anotalo en las notas del pedido.',
      products: topSalesWithMemory(products, 4, memory, menuData, n),
      fallbackKeys: ['catalog', 'order_hint', 'main'],
    };
  }

  if (preferFastFoodIntent(n)) {
    if (/\b(horario|precio|delivery|cuanto|cuánto)\b/.test(n)) return null;
    const vis = visibleMenuProducts(products);
    const hits = vis
      .filter((p) => semanticTagsForProduct(p, menuData).is_fast)
      .sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0))
      .slice(0, 8);
    if (hits.length) {
      return {
        text: '⚡ Estas suelen ser más directas según la ficha (snacks, bebidas o clásicos rápidos) 👇',
        products: hits,
        fallbackKeys: defaultFallback,
      };
    }
  }

  if (craving) {
    if (/\b(horario|precio|delivery|cuanto)\b/.test(n)) return null;
    const hits = productsByCraving(products, craving);
    const label = { dulce: 'dulce', salado: 'salado', crujiente: 'crujiente', queso: 'queso' }[craving];
    if (hits.length) {
      return {
        text: `Uy, se me ocurre esto con onda ${label} 👇`,
        products: hits,
        fallbackKeys: defaultFallback,
      };
    }
    return {
      text: `No veo algo muy marcado como ${label} en las fichas del menú; probá en la carta completa o pedime por nombre.`,
      products: null,
      fallbackKeys: ['catalog', 'cheap_hint', 'main'],
    };
  }

  if (detectGenericRecommendationQuery(n, trimmed)) {
    if (/\b(horario|precio|delivery|cuanto|cuánto)\b/.test(n)) return null;
    const picks = pickRecommendationProducts(products, resolvedCtx, 6, memory, menuData, n);
    if (picks.length) {
      const v = pickVariant('recommendation_copy', AB_RECOMMENDATION_COPY);
      const ctxNote =
        resolvedCtx.length >= 1
          ? ' Mezclo lo que estábamos viendo con lo más pedido del menú.'
          : ' Esto es de lo que más piden y suele salir bien.';
      let outText;
      if (v.id === 'B') {
        const base = v.text.replace(/\s*👇\s*$/, '').trimEnd();
        outText = `${base}${ctxNote} 👇`;
      } else {
        outText = `${v.text}${ctxNote} 👇`;
      }
      return {
        text: outText,
        products: picks,
        fallbackKeys: defaultFallback,
        assistantAbAnalytics: { ab_test_key: 'recommendation_copy', ab_variant_id: v.id },
      };
    }
    return {
      text: 'Tengo el menú cargado; abrilo un toque y decime qué te tinca, o pedime por tipo (hamburguesa, sushi, algo dulce…).',
      products: null,
      fallbackKeys: ['catalog', 'categories_hint', 'order_hint', 'main'],
    };
  }

  if (
    /\b(cual\s+recomiendas|cuál\s+recomiendas|que\s+es\s+mejor|qué\s+es\s+mejor|que\s+conviene\s+mas|qué\s+conviene\s+más|que\s+convendria|cuál\s+elegirias)\b/.test(
      n
    )
  ) {
    let a;
    let b;
    if (resolvedCtx.length >= 2) {
      a = resolvedCtx[0];
      b = resolvedCtx[1];
    } else {
      const top2 = topSalesProducts(products, 2);
      if (top2.length >= 2) {
        a = top2[0];
        b = top2[1];
      }
    }
    if (a && b) {
      return {
        text: `Te lo dejo simple:\n\n${compareTwoProductsCopy(a, b)}`,
        products: [a, b],
        fallbackKeys: defaultFallback,
      };
    }
    return {
      text: 'Para comparar necesito dos opciones claras. Elegí dos del menú o pedime por nombre y te digo cuál me hace más sentido.',
      products: null,
      fallbackKeys: ['catalog', 'categories_hint', 'main'],
    };
  }

  if (
    /\b(mejor\s+no|otra\s+cosa|mejor\s+otra|cambio\s+de\s+idea|dejalo|déjalo|no\s+quiero\s+eso)\b/.test(n)
  ) {
    return {
      text: 'Tranqui, pasa nada 😊 Miramos otra cosa cuando quieras.',
      products: null,
      fallbackKeys: ['catalog', 'categories_hint', 'order_hint', 'main'],
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  if (
    /\b(vale\s+la\s+pena|conviene|esta\s+caro|está\s+caro|es\s+caro|sale\s+caro|muy\s+caro|es\s+grande|porcion\s+grande|porción\s+grande)\b/.test(
      n
    )
  ) {
    if (bestProduct) {
      const price = Number(bestProduct.price) || 0;
      const vis = visibleProductsList(products).filter((p) => p.id !== bestProduct.id);
      const cheaper = [...vis]
        .filter((p) => (Number(p.price) || 0) > 0 && (Number(p.price) || 0) < price)
        .sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0))
        .slice(0, 4);
      const bits = [
        `${bestProduct.name} está al precio que carga el local ($${price.toLocaleString('es-CL')}). En la carta suele ir con lo que ves en nombre y descripción.`,
      ];
      if (/\b(grande|porcion|porción)\b/.test(n)) {
        bits.push('El tamaño exacto no siempre viene en la ficha; si dudas, anotalo en las notas del pedido y el local te confirma.');
      } else {
        bits.push('Si buscás algo más accesible, mirá estas alternativas 👇');
      }
      return {
        text: bits.join('\n\n'),
        products: cheaper.length ? [bestProduct, ...cheaper] : [bestProduct],
        fallbackKeys: cheaper.length ? ['cheap_hint', 'catalog', 'main'] : ['catalog', 'main'],
      };
    }
    if (/\b(vale\s+la\s+pena|conviene)\b/.test(n)) {
      return {
        text: 'Depende del hambre y el momento 😄 Si querés algo que rinda más por precio, mirá lo más pedido o lo más barato.',
        products: topSalesProducts(products, 4),
        fallbackKeys: ['cheap_hint', 'catalog', 'main'],
      };
    }
    return null;
  }

  if (/\b(es\s+bueno|es\s+buena|recomendad|que\s+tal\s+es|qué\s+tal\s+es|que\s+onda\s+es)\b/.test(n)) {
    if (bestProduct) {
      const sc = Number(bestProduct.sales_count) || 0;
      const social =
        sc >= 3
          ? `La gente lo pide bastante acá (${sc} ventas registradas en este menú). `
          : 'No tengo muchas ventas registradas para este ítem, pero está en carta. ';
      return {
        text: `${social}Te dejo la ficha para que veas si te tinca 👇`,
        products: [bestProduct],
        fallbackKeys: defaultFallback,
      };
    }
    const top = topSalesProducts(products, 4);
    if (top.length) {
      return {
        text: 'Estas son de las que más salen; probá una y contame 👇',
        products: top,
        fallbackKeys: defaultFallback,
      };
    }
    return {
      text: 'No tengo una recomendación con datos claros acá; abrí el menú y elegí lo que te llame.',
      products: null,
      fallbackKeys: ['catalog', 'main'],
    };
  }

  if (
    /\b(alcanza\s+para|alcanza|cuantas\s+personas|cuántas\s+personas|para\s+cuantos|para\s+cuántos|comen\s+entre)\b/.test(
      n
    )
  ) {
    if (bestProduct) {
      const name = bestProduct.name || 'Este producto';
      if (productSuggestsSharingOrGroup(bestProduct, menuData)) {
        return {
          text: `Por cómo viene descrito ${name}, va más tirado a compartir o pedir en grupo. Si son dos nomás, fijate si hay opciones “pa dos” en el menú.`,
          products: [bestProduct],
          fallbackKeys: ['catalog', 'duo_hint', 'main'],
        };
      }
      return {
        text: `En la ficha de ${name} no dice claro si alcanza para dos. Si dudan, yo pediría una por persona o algo marcado para compartir.`,
        products: [bestProduct],
        fallbackKeys: ['catalog', 'order_hint', 'main'],
      };
    }
    return {
      text: 'Decime el nombre del producto como figura en el menú y te digo si suena a porción individual o para compartir.',
      products: null,
      fallbackKeys: ['catalog', 'main'],
    };
  }

  return null;
}

/**
 * Familias alimentarias inferidas desde fichas (anchors) para sugerir complementos.
 */
function detectBaseFoodFamiliesFromBlob(blob) {
  const b = normalizeAssistantText(blob || '');
  const fam = [];
  if (/\b(hamburguesa|burger|hamburg|smash)\b/.test(b)) fam.push('burger');
  if (/\b(sushi|maki|nigiri|uramaki|sashimi|temaki|roll\b|rolls)\b/.test(b)) fam.push('sushi');
  if (/\b(pizza|piza)\b/.test(b)) fam.push('pizza');
  if (/\b(pollo|broaster|alitas|wings|nugget|crispy)\b/.test(b)) fam.push('chicken');
  if (/\b(taco|burrito|nacho|quesadilla)\b/.test(b)) fam.push('mex');
  if (/\b(completo|hotdog|hot dog|vienesa)\b/.test(b)) fam.push('completo');
  if (/\b(churrasco|parrilla|lomito|asado|entraña)\b/.test(b)) fam.push('grill');
  return [...new Set(fam)];
}

/**
 * Qué complementos ya vienen cubiertos en la ficha del producto principal (esta respuesta).
 */
function detectAlreadyIncludedComplementsFromBlob(blob) {
  const b = normalizeAssistantText(blob || '');
  return {
    drink: /\b(incluye\s+bebida|bebida\s+incluida|con\s+bebida|\+bebida|incluye\s+gaseosa|gaseosa\s+incluida|vaso\s+de\s+bebida|lata\s+incl|refresco\s+incl|jugo\s+incl|bebid\w*\s+gratis)\b/.test(
      b
    ),
    fries: /\b(incluye\s+papas|papas\s+incluidas|con\s+papas|viene\s+con\s+papas|\+papas|papas\s+fritas\s+incl|men[uú]\s+con\s+papas|menu\s+con\s+papas|acompa[nñ]a\s+papas)\b/.test(
      b
    ),
    accompaniment: /\b(incluye\s+acompa[nñ]amiento|acompa[nñ]amiento\s+incluido|incluye\s+guarn|guarnicion\s+incl|guarnición\s+incl|incluye\s+acompa[nñ]ante)\b/.test(
      b
    ),
    combo: /\b(es\s+combo|menu\s+combo|men[uú]\s+combo|promo\s+combo|combo\s+que\s+incluye|incluye\s+combo|tipo\s+combo|viene\s+en\s+combo|pedido\s+combo|incluye\s+men[uú]\s+completo)\b/.test(
      b
    ),
  };
}

function suggestionConflictsWithIncluded(candidateBlob, included) {
  if (!included || typeof included !== 'object') return false;
  const c = normalizeAssistantText(candidateBlob || '');
  const isDrink =
    /\b(bebida\b|gaseosa|jugo\b|coca\b|sprite|pepsi|schop|agua\s+mineral|energizante)\b/.test(c);
  const isFries = /\b(papa|papas|frita|fritas|porcion\s+de\s+papas|porción\s+de\s+papas)\b/.test(c);
  const isComboLike =
    /\b(combo\b|men[uú]\s+combo|menu\s+combo|pack\s+familiar|pack\s+duo|pack\s+dúo|pack\s+para\s+2)\b/.test(
      c
    );

  if (included.drink && isDrink) return true;
  if (included.fries && isFries) return true;
  if (included.combo && isComboLike) return true;
  if (included.accompaniment && (isFries || isDrink)) return true;
  return false;
}

function complementKeywordPatternForFamilies(families) {
  const set = new Set([
    'bebida',
    'bebidas',
    'gaseosa',
    'jugo',
    'coca',
    'sprite',
    'pepsi',
    'schop',
    'agua',
    'papa',
    'papas',
    'frita',
    'fritas',
    'combo',
    'pack',
    'extra',
    'extras',
    'entrada',
    'entradas',
    'ensalada',
    'nugget',
    'aros',
    'dip',
    'salsa',
    'miso',
    'edamame',
    'palitos',
    'papas fritas',
  ]);
  if (families.includes('burger')) {
    ['cheddar', 'bacon', 'queso extra', 'doble'].forEach((x) => set.add(normalizeAssistantText(x)));
  }
  if (families.includes('sushi')) {
    ['gyoza', 'tempura', 'wantan'].forEach((x) => set.add(normalizeAssistantText(x)));
  }
  if (families.includes('pizza')) {
    ['palitos', 'dips', 'borde'].forEach((x) => set.add(normalizeAssistantText(x)));
  }
  const parts = [...set]
    .map((x) => normalizeAssistantText(String(x)))
    .filter((x) => x.length >= 2)
    .map(escapeRegExp);
  if (!parts.length) return /\b(bebida|papas|combo)\b/i;
  return new RegExp(`\\b(${parts.join('|')})\\b`, 'i');
}

function mergeCheckoutCartFallback(keys, memory) {
  if (memory?.lastIntent !== 'order') return keys || [];
  const k = [...(keys || [])];
  const rest = k.filter((x) => x !== 'checkout_cart');
  return ['checkout_cart', ...rest];
}

/**
 * Sugerencias de complemento / grupo / precio sin reemplazar la respuesta principal.
 */
export function applyContextualSuggestionLayer(ans, menuData, flowContext) {
  if (!ans || typeof ans !== 'object') return ans;
  if (!ans.products?.length) return ans;

  const memory = flowContext?.sessionMemory || {};
  const menuProducts = menuData?.products || [];
  const vis = visibleMenuProducts(menuProducts);
  if (vis.length < 2) return ans;

  const exclude = new Set();
  for (const p of ans.products) exclude.add(String(p.id));
  for (const id of memory.shownContextualProductIds || []) exclude.add(String(id));

  const pmap = new Map(vis.map((p) => [String(p.id), p]));
  const anchorIdSet = new Set([
    ...ans.products.map((p) => String(p.id)),
    ...(memory.lastProducts || []).map((id) => String(id)),
  ]);
  const anchors = [...anchorIdSet]
    .map((id) => pmap.get(id))
    .filter(Boolean)
    .slice(0, 12);
  const upsellHints = [
    ...new Set(
      anchors
        .flatMap((p) => getProductUpsellItems(p))
        .map((s) => normalizeAssistantText(String(s || '')).trim())
        .filter((s) => s.length >= 3)
    ),
  ];
  const anchorBlob = anchors.map((p) => productIngredientSearchBlob(p)).join(' ');
  const familiesSet = new Set(detectBaseFoodFamiliesFromBlob(anchorBlob));
  for (const p of anchors) {
    const t = semanticTagsForProduct(p, menuData);
    for (const cf of complementFamiliesFromSemanticFamily(t.family)) {
      if (cf) familiesSet.add(cf);
    }
  }
  const families = [...familiesSet];
  const complementRe = complementKeywordPatternForFamilies(families);

  const mainProductsBlob = ans.products.map((p) => productIngredientSearchBlob(p)).join(' ');
  const alreadyIncluded = detectAlreadyIncludedComplementsFromBlob(mainProductsBlob);
  for (const p of ans.products) {
    const t = semanticTagsForProduct(p, menuData);
    if (t.has_drink) alreadyIncluded.drink = true;
    if (t.has_fries) alreadyIncluded.fries = true;
    if (t.is_combo) alreadyIncluded.combo = true;
    if (productIncludedHasDrink(p)) alreadyIncluded.drink = true;
    if (productIncludedHasFries(p)) alreadyIncluded.fries = true;
  }

  const party2 = Number(memory.partySize) >= 2;
  const wantCheap = memory.priceSensitivity === 'cheap';

  const candidates = vis.filter((p) => !exclude.has(String(p.id)));
  if (!candidates.length) return ans;

  const scoreOne = (p) => {
    const blob = productIngredientSearchBlob(p);
    if (suggestionConflictsWithIncluded(blob, alreadyIncluded)) {
      const price = Number(p.price) || 999999;
      const sales = Number(p.sales_count) || 0;
      return { p, score: 0, price, sales };
    }
    let score = 0;
    if (complementRe.test(blob)) score += 4;
    if (party2) {
      if (/\b(combo|pack|compartir|familiar|duo|dúo|2\s*pers|dos\s+personas|para\s+2)\b/i.test(blob)) {
        score += 6;
      }
      if (productSuggestsSharingOrGroup(p, menuData)) score += 5;
      const st = semanticTagsForProduct(p, menuData);
      if (st.is_combo) score += 2;
    } else if (/\b(combo|pack)\b/i.test(blob)) {
      score += 1;
    }
    if (/\b(postre|helado|brownie|torta|alfajor)\b/.test(blob) && score < 4) score -= 1;
    if (alreadyIncluded.fries && families.includes('burger') && !alreadyIncluded.drink) {
      if (/\b(bebida|gaseosa|jugo|coca|sprite|pepsi|schop|agua)\b/i.test(blob)) score += 3;
    }
    if (alreadyIncluded.fries && families.includes('burger')) {
      if (/\b(extra|doble|bacon|cheddar|carne\s+extra)\b/i.test(blob)) score += 2;
    }
    if (upsellHints.length) {
      const hit = upsellHints.some((hint) => hint.length >= 4 && blob.includes(hint));
      if (hit) score += 3;
    }
    const price = Number(p.price) || 999999;
    const sales = Number(p.sales_count) || 0;
    return { p, score, price, sales };
  };

  let scored = candidates.map(scoreOne).filter((x) => x.score > 0);

  if (!scored.length && party2) {
    scored = candidates
      .map((p) => {
        const blob = productIngredientSearchBlob(p);
        if (suggestionConflictsWithIncluded(blob, alreadyIncluded)) {
          const price = Number(p.price) || 999999;
          const sales = Number(p.sales_count) || 0;
          return { p, score: 0, price, sales };
        }
        let score = 0;
        if (/\b(combo|pack|compartir|familiar)\b/i.test(blob)) score += 4;
        if (productSuggestsSharingOrGroup(p, menuData)) score += 4;
        const price = Number(p.price) || 999999;
        const sales = Number(p.sales_count) || 0;
        return { p, score, price, sales };
      })
      .filter((x) => x.score > 0);
  }

  if (!scored.length) {
    const out = { ...ans, fallbackKeys: mergeCheckoutCartFallback(ans.fallbackKeys, memory) };
    return out;
  }

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (wantCheap) return a.price - b.price;
    return b.sales - a.sales;
  });

  const cap = Math.min(3, scored.length);
  const sug = scored.slice(0, cap).map((x) => x.p);

  let line = 'La mayoría le suma esto 👇';
  if (party2 && families.length) {
    line = 'Para el grupo, esto suele cerrar bien 👇';
  } else if (party2) {
    line = 'Para varias personas, mirá también esto 👇';
  }

  const nextKeys = mergeCheckoutCartFallback(ans.fallbackKeys, memory);

  return {
    ...ans,
    textAfterProducts: ans.textAfterProducts ? `${ans.textAfterProducts}\n\n${line}` : line,
    suggestedProducts: sug,
    fallbackKeys: nextKeys,
  };
}

/**
 * Ajuste de tono y horario acordado según memoria de sesión (sin persistencia).
 */
export function applySessionMemoryToAnswer(ans, memory, _trimmed) {
  if (!ans || typeof ans !== 'object') return ans;
  if (!memory || typeof memory !== 'object') return ans;

  const out = { ...ans };
  let text = out.text;

  if (text && memory.mood === 'happy' && !/^qué bueno\b/i.test(text)) {
    text = `Qué bueno 🙌 ${text}`;
  }
  if (
    text &&
    memory.mood === 'frustrated' &&
    !out.containmentIncluded &&
    !(Number(out.frustrationLevel) > 0)
  ) {
    text = `Te lo dejo claro y al tiro: ${text}`;
  }

  if (memory.scheduledTime && String(memory.scheduledTime).trim()) {
    const hora = String(memory.scheduledTime).trim();
    if (text) {
      text = `${text}\n\nPerfecto, lo dejamos para las ${hora} 👍`;
    } else {
      text = `Perfecto, lo dejamos para las ${hora} 👍`;
    }
    const note = `Horario indicado en el chat: ${hora}`;
    out.orderNoteAppend = out.orderNoteAppend ? `${out.orderNoteAppend}\n${note}` : note;
  }

  out.text = text;
  return out;
}

function isDirectSideBundleQuestion(n) {
  return (
    (/\b(papa|papas|frita|fritas|bebida|gaseosa|jugo|refresco)\b/.test(n) &&
      /\b(viene|vienen|inclu|incluye|trae|traen|lleva|llevan)\b/.test(n)) ||
    /\btrae\s+papa|\bviene\s+con\s+papa/.test(n)
  );
}

function isBurgerComesWithQuestion(n) {
  if (!/\b(hamburguesa|hamburguesas|burger|burgers|smash)\b/.test(n)) return false;
  const mentionsSide = /\b(papa|papas|frita|fritas|bebida|gaseosa|jugo|refresco|acompañ|acompan|combo)\b/.test(
    n
  );
  const asksInclusion =
    /\b(vienen|viene|inclu|incluye|trae|traen|lleva|llevan|van\s+con|es\s+con)\b/.test(n) ||
    /\bvienen\s+con\b/.test(n) ||
    /\bviene\s+con\b/.test(n);
  if (mentionsSide && asksInclusion) return true;
  if (asksInclusion && /\b(o|y)\b/.test(n) && mentionsSide) return true;
  return false;
}

function isBurgerWantPapasYBebidaIntent(n) {
  if (!/\b(hamburguesa|hamburguesas|burger|burgers)\b/.test(n)) return false;
  if (!/\b(papa|papas|frita|fritas)\b/.test(n)) return false;
  if (!/\b(bebida|gaseosa|jugo|refresco|coca|sprite|pepsi)\b/.test(n)) return false;
  return true;
}

function isBurgerWantSoloQuery(n) {
  if (!/\b(sola|solo)\b/.test(n)) return false;
  return /\b(la|lo|quiero|pedir|hamburguesa|burger|esa|esta|ese|eso)\b/.test(n);
}

/**
 * Composición / combo: hamburguesas (agregado histórico) + cualquier producto en foco cuando aplica.
 */
export function tryBurgerBundleCompositionAnswer(n, products, menuData, memory, bestProduct, flowContext) {
  const vis = visibleMenuProducts(products);
  const burgers = vis.filter((p) => productIsBurgerish(p));

  const ctxResolved = resolveProductsByIds(products, [
    ...(flowContext?.lastSuggestedProductIds || []),
    ...(memory?.lastProducts || []),
  ]).filter((p) => p.availability !== 'hidden');

  const ctxBurger =
    ctxResolved.find((p) => productIsBurgerish(p)) ||
    (bestProduct && productIsBurgerish(bestProduct) ? bestProduct : null);

  const focusProduct =
    ctxBurger ||
    (burgers.length === 1 ? burgers[0] : null) ||
    (bestProduct && bestProduct.availability !== 'hidden' ? bestProduct : null) ||
    (ctxResolved.length ? ctxResolved[0] : null);

  const aggregateMention = /\b(hamburguesa|hamburguesas|burger|burgers|smash)\b/.test(n);
  const soloIntent =
    isBurgerWantSoloQuery(n) ||
    /\b(viene\s+sola|va\s+sola|la\s+tienen\s+sola)\b/.test(n) ||
    (/\b(sola|solo)\b/.test(n) && /\b(la|lo|quiero|pedir|esa|esta|ese|eso)\b/.test(n));

  const business = menuData?.business || {};

  if ((isDirectSideBundleQuestion(n) || soloIntent) && !aggregateMention && focusProduct) {
    const strat = pickAssistantCompositionStrategy(focusProduct, business, menuData);
    return strat.answerSingleComesWith(focusProduct, n, soloIntent, menuData, business);
  }

  if (!burgers.length) return null;

  if (soloIntent && (ctxBurger || /\b(hamburguesa|burger|smash)\b/.test(n))) {
    const target = ctxBurger || (burgers.length === 1 ? burgers[0] : null);
    if (target) {
      const items = getProductIncludedItems(target);
      const bundled =
        items.length > 0 ||
        productHasExplicitAssistantBundle(target) ||
        productIncludedHasFries(target) ||
        productIncludedHasDrink(target);
      if (bundled) {
        const withList = items.length ? items.join(', ') : getProductIncludedSummary(target);
        const lines = [
          `No las manejamos solas: ${target.name || 'esta opción'} viene con ${withList || 'acompañamiento incluido'}.`,
          'Te muestro algunas.',
        ];
        return {
          text: lines.join('\n\n'),
          products: burgers.slice(0, 8),
          fallbackKeys: ['catalog', 'order_hint', 'main'],
        };
      }
    }
  }

  if (isBurgerComesWithQuestion(n)) {
    return burgerStrategy.answerBurgerComesWithAggregate(burgers, n);
  }

  if (isBurgerWantPapasYBebidaIntent(n)) {
    return burgerStrategy.answerBurgerWantPapasYBebida(burgers);
  }

  return null;
}

/**
 * @param {object} menuData — respuesta de GET /menu/:id
 * @param {string} rawText
 * @param {{ skipSocialLayer?: boolean, skipConversationalLayer?: boolean, flowContext?: { currentStep?: string, servicePreference?: 'delivery'|'pickup'|null, lastSuggestedProductIds?: string[], sessionMemory?: object, cartItemCount?: number }, treatAsOrderPartyStep?: boolean }} [options]
 * @returns {{ text: string, products: object[]|null, suggestedProducts?: object[], textAfterProducts?: string, fallbackKeys: string[], followUp?: object, orderChoice?: string, containmentIncluded?: boolean, frustrationLevel?: number, orderNoteAppend?: string|null }}
 */
/** Promos, barato/caro, dietas, composición, menciones fuertes y lookup simple. */
export function tryProductMenuIntents({
  n,
  trimmed,
  menuData,
  business,
  products,
  promos,
  defaultFallback,
  ranked,
  bestProduct,
  memory,
  options,
}) {
  if (/\b(promo|promos|promocion|promoción|oferta|2x1|dos\s*x\s*uno)\b/.test(n)) {
    if (!promos.length) {
      return {
        text: '🏷️ Ahora no figuran promociones activas cargadas en el menú.',
        products: null,
        fallbackKeys: defaultFallback,
      };
    }
    const lines = promos.slice(0, 8).map((p) => {
      const price = p.special_price ?? p.price;
      const priceStr =
        price != null && price !== '' ? ` — $${Number(price).toLocaleString('es-CL')}` : '';
      const desc = p.description ? `\n   ${String(p.description).slice(0, 120)}` : '';
      return `• ${p.name || 'Promo'}${priceStr}${desc}`;
    });
    return {
      text: `🏷️ Promos que figuran activas:\n\n${lines.join('\n')}`,
      products: null,
      fallbackKeys: defaultFallback,
    };
  }

  if (/\b(barato|barata|economico|económico|mas barato|más barato)\b/.test(n)) {
    const visible = products.filter((p) => p.availability !== 'hidden');
    const sorted = [...visible].sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
    const top = sorted.slice(0, 3);
    if (!top.length) {
      return { text: 'No tengo productos para listar precios acá.', products: null, fallbackKeys: defaultFallback };
    }
    return {
      text: '💰 Estos son de los más económicos que figuran en el menú:',
      products: top,
      fallbackKeys: defaultFallback,
    };
  }

  if (/\b(caro|cara|caros|sale caro|muy caro)\b/.test(n)) {
    return {
      text:
        '💬 Entiendo. Los precios son los que ves en cada producto. Podés mirar opciones más económicas o las promos activas.',
      products: null,
      fallbackKeys: ['cheap_hint', 'promo_hint', 'catalog', 'main'],
    };
  }

  if (/\b(vegano|vegana|veganos|veganas)\b/.test(n)) {
    const subHits = findDietaryMatchesSubstrings(products, ['vegan', 'vegana', 'vegano', 'veganos']);
    const tagHits = visibleMenuProducts(products).filter((p) => semanticTagsForProduct(p, menuData).is_vegan);
    const hits = mergeProductListsPreservingOrder(subHits, tagHits).sort(
      (a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0)
    );
    if (!hits.length) {
      return { text: '🌱 No veo opciones veganas en el menú cargado.', products: null, fallbackKeys: defaultFallback };
    }
    return {
      text: '🌱 Sí, encontré opciones que en la ficha mencionan algo vegano 👇',
      products: hits.slice(0, 8),
      fallbackKeys: defaultFallback,
    };
  }

  if (/\b(vegetariano|vegetariana|vegetarian)\b/.test(n)) {
    const subHits = findDietaryMatchesSubstrings(products, ['vegetarian', 'vegetariano', 'vegetariana', 'vegetar']);
    const tagHits = visibleMenuProducts(products).filter((p) => semanticTagsForProduct(p, menuData).is_vegetarian);
    const hits = mergeProductListsPreservingOrder(subHits, tagHits).sort(
      (a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0)
    );
    if (!hits.length) {
      return { text: '🥬 No veo opciones vegetarianas en el menú cargado.', products: null, fallbackKeys: defaultFallback };
    }
    return {
      text: '🥬 Sí, encontré opciones vegetarianas en la ficha 👇',
      products: hits.slice(0, 8),
      fallbackKeys: defaultFallback,
    };
  }

  if (/\b(sin gluten|gluten|celiac|celíac|celiaco|celiaca)\b/.test(n)) {
    const subHits = findDietaryMatchesSubstrings(products, ['sin gluten', 'gluten', 'celiac', 'celiaco', 'celiaca']);
    const tagHits = visibleMenuProducts(products).filter((p) => semanticTagsForProduct(p, menuData).is_gluten_free);
    const hits = mergeProductListsPreservingOrder(subHits, tagHits).sort(
      (a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0)
    );
    if (!hits.length) {
      const at = getAssistantTraining(business);
      if (at?.allergies_info) {
        return {
          text:
            '🌾 No encontré productos etiquetados sin gluten en la carta cargada. El local dejó esta información sobre alergias:\n\n' +
            String(at.allergies_info).trim() +
            '\n\nSi tenés duda con un plato puntual, conviene confirmarlo al hacer el pedido.',
          products: null,
          fallbackKeys: defaultFallback,
        };
      }
      return { text: '🌾 No veo opciones sin gluten en el menú cargado.', products: null, fallbackKeys: defaultFallback };
    }
    return {
      text: '🌾 Sí, encontré opciones que en la ficha mencionan sin gluten o gluten 👇',
      products: hits.slice(0, 8),
      fallbackKeys: defaultFallback,
    };
  }

  const comboAns = answerImplicitComboIntent(n, trimmed, products, defaultFallback, menuData);
  if (comboAns) return comboAns;

  if (isIngredientAvailabilityQuery(n, trimmed)) {
    const ingAns = answerIngredientAvailabilityQuestion(trimmed, n, products, defaultFallback, menuData);
    if (ingAns) return ingAns;
  }

  const asksIngredients =
    /\b(que lleva|qué lleva|que trae|qué trae|ingredientes|de que esta|de qué está)\b/.test(n) ||
    (/\b(lleva|trae|inclu|viene)\b/.test(n) && /\b(papa|papas|bebida|gaseosa|queso)\b/.test(n)) ||
    /\b(viene\s+con\s+algo|trae\s+algo|incluye\s+algo)\b/.test(n);
  const asksRemove = /\b(sacar|quitar|remover|omitir|sin\s+[a-záéíóúñ0-9]{2,})\b/.test(n);
  const asksAdd = /\b(agregar|añadir|extra|otra carne|más carne|mas carne|doble)\b/.test(n);
  const asksPersonalizationChat =
    /\b(puedo\s+cambiar|se\s+puede\s+cambiar|personaliz|modificar\s+ingredient)\b/.test(n);

  if ((asksIngredients || asksRemove || asksAdd || asksPersonalizationChat) && bestProduct) {
    return {
      text: describeProductComposition(bestProduct, n, menuData, business),
      products: [bestProduct],
      fallbackKeys: defaultFallback,
    };
  }

  if (asksIngredients || asksRemove || asksAdd || asksPersonalizationChat) {
    const hint =
      ranked.length > 0 && ranked[0].score < 3
        ? `¿Te referís a "${ranked[0].product.name}"? Probá escribiendo el nombre como sale en el menú.`
        : 'Decime el nombre del producto como figura en el menú, o revisalo en la carta.';
    return { text: `🍽️ ${hint}`, products: null, fallbackKeys: defaultFallback };
  }

  const mention = resolveStrongProductMention(trimmed, products);
  if (mention?.products?.length) {
    return { text: mention.headline, products: mention.products, fallbackKeys: defaultFallback };
  }

  if (isSimpleMenuLookupQuery(trimmed, n)) {
    const simpleAns = answerSimpleMenuLookup(trimmed, products, defaultFallback, menuData);
    if (simpleAns) return simpleAns;
  }

  return null;
}

